"use client";

import { type ReactNode, useEffect } from "react";
import SignOutButton from "src/components/app/SignOutButton/SignOutButton";
import StatusIllustration from "src/components/app/StatusIllustration/StatusIllustration";
import StatusPage from "src/components/app/StatusPage/StatusPage";
import { useSupportDialog } from "src/components/app/SupportDialog/useSupportDialog";
import Button from "src/components/common/Button/Button";
import Typography from "src/components/common/Typography/Typography";
import { useOptionalAuth } from "src/lib/contexts/AuthContext";
import { reportErrorShown } from "src/lib/ErrorReporting";
import { type ErrorType, getErrorType } from "src/lib/RouteStatusHead";
import {
  isReloadingForCurrentBuild,
  isStaleBuildError,
  reloadForCurrentBuild,
} from "src/lib/StaleBuild";
import { useExtracted } from "#i18n";

export type { ErrorType } from "src/lib/RouteStatusHead";

export interface ErrorDisplayInfo {
  title: string;
  message: string;
  art: ReactNode;
}

export interface ErrorPageProps {
  error?: Error & { digest?: string };
  type?: ErrorType;
  /** Re-renders the failed route; provided by the route error boundaries (`routes/-components/RouteBoundaries.tsx`). */
  retry?: () => void;
  /** `inline` fills a layout's content area, for errors caught below the root layout. */
  variant?: "page" | "inline";
}

const ErrorPage = (props: ErrorPageProps) => {
  const translateMessage = useExtracted();
  const { error, type, retry, variant } = props;
  const isLoggedIn = Boolean(useOptionalAuth()?.authState);
  const feedback = useSupportDialog(error);

  // The document title for each state comes from the route head (ERROR_PAGE_TITLES).
  const errorType = getErrorType(error, type);

  useEffect(() => {
    if (!error) {
      return;
    }
    // Code from a build that has since been replaced: load the current one instead of failing.
    if (isReloadingForCurrentBuild() || (isStaleBuildError(error) && reloadForCurrentBuild())) {
      return;
    }
    console.error(error);
    // Missing pages, access and connectivity problems are expected; anything else is a bug to fix.
    if (errorType === "unknown") {
      reportErrorShown(error);
    }
  }, [error, errorType]);

  const errorDisplayInfo: Record<ErrorType, ErrorDisplayInfo> = {
    "not-found": {
      title: translateMessage("Page not found"),
      message: translateMessage(
        "This page doesn't exist or has moved. Check the link, or head back to somewhere familiar.",
      ),
      art: <StatusIllustration scene="not-found" code="404" />,
    },
    unauthorized: {
      title: translateMessage("You don't have access to this page"),
      message: translateMessage(
        "Ask a project administrator to give you access, or sign in with a different account.",
      ),
      art: <StatusIllustration scene="unauthorized" code="403" />,
    },
    unknown: {
      title: translateMessage("Something went wrong"),
      message: translateMessage(
        "We hit an unexpected problem loading this page. Try again, and contact support if it keeps happening.",
      ),
      art: <StatusIllustration scene="unknown" code="500" />,
    },
    "network-error": {
      title: translateMessage("You're offline"),
      message: translateMessage(
        "We couldn't reach CalmLens. Check your internet connection, then try again.",
      ),
      art: <StatusIllustration scene="network-error" />,
    },
  };
  const displayInfo = errorDisplayInfo[errorType];

  const homeButton = (variant: "normal" | "outline") => (
    <Button
      label={isLoggedIn ? translateMessage("Go to portal") : translateMessage("Go home")}
      href={isLoggedIn ? "/portal" : "/"}
      variant={variant}
    />
  );
  const retryButton = (
    <Button
      label={translateMessage("Try again")}
      onClick={() => (retry ? retry() : window.location.reload())}
    />
  );

  const actions: Record<ErrorType, ReactNode> = {
    "not-found": (
      <>
        {homeButton("normal")}
        <Button
          label={translateMessage("Go back")}
          variant="outline"
          onClick={() => window.history.back()}
        />
      </>
    ),
    unauthorized: (
      <>
        {homeButton("normal")}
        {isLoggedIn ? <SignOutButton /> : null}
      </>
    ),
    unknown: (
      <>
        {retryButton}
        {homeButton("outline")}
        <Button
          label={translateMessage("Report a problem")}
          variant="outline"
          onClick={feedback.open}
          onMouseEnter={feedback.preload}
          onFocus={feedback.preload}
          loading={feedback.capturing}
        />
      </>
    ),
    "network-error": retryButton,
  };

  return (
    <StatusPage
      title={displayInfo.title}
      art={displayInfo.art}
      actions={actions[errorType]}
      variant={variant}
    >
      <Typography>{displayInfo.message}</Typography>
      {error?.digest ? (
        <Typography variant="bodySmall" tone="muted">
          {translateMessage("Reference: {digest}", { digest: error.digest })}
        </Typography>
      ) : null}
      {errorType === "unknown" ? feedback.dialog : null}
    </StatusPage>
  );
};

export default ErrorPage;
